import { getCookie } from "utils/cookieManagement";
import {
  getStoredApiKey,
  isUserApiKey,
  readSessionUserData,
  resolveBootstrapApiKey,
} from "utils/auth/apiKeyStore";

const SESSION_COOKIES = ["ses_userdata", "ses_name", "ses_sid", "ses_token"];

/**
 * The session a hard load can restore from storage, by kind:
 *
 *  - `cookie`: the legacy session cookies, complete and decodable (in
 *    development `ses_userdata` alone). They carry the profile, so they win
 *    when a stored key exists too; `storedApiKey` still reports that key.
 *  - `apiKey`: a user api key in localStorage (`getStoredApiKey`: not blank,
 *    not a stringified null, not the site's anonymous key) and no usable
 *    cookie session.
 */
export type StoredSession =
  | { kind: "cookie"; userData: any; storedApiKey: string | null }
  | { kind: "apiKey"; storedApiKey: string };

/**
 * THE answer to "is there a stored session, and of which kind". Both restore
 * paths (`AccountWrapper`, `useAuthInitialization`) branch on it, so they can
 * never disagree on what a session is. Storage that throws (private mode,
 * blocked cookies) counts as no session.
 */
export const classifyStoredSession = (): StoredSession | null => {
  try {
    const storedApiKey = getStoredApiKey();
    const cookies = SESSION_COOKIES.map(getCookie);
    const cookiesPresent =
      cookies.every(Boolean) ||
      (process.env.NODE_ENV === "development" && Boolean(cookies[0]));
    const userData = cookiesPresent ? readSessionUserData() : null;

    if (userData) return { kind: "cookie", userData, storedApiKey };
    if (storedApiKey) return { kind: "apiKey", storedApiKey };
    return null;
  } catch {
    return null;
  }
};

/**
 * The user key the bootstrap is about to put in the store (localStorage, else
 * the session cookie), or null when the account has none to restore. Lets a
 * page show "loading" instead of "you have no key" for that first render.
 */
export const pendingBootstrapApiKey = (): string | null => {
  try {
    const key = resolveBootstrapApiKey();
    return isUserApiKey(key) ? key : null;
  } catch {
    return null;
  }
};
